import React, {useCallback, useEffect, useRef, useState} from 'react';
import {ModelDTO, UserDTO} from 'hemwb-api';
import {useDispatch} from 'react-redux';
import {Button, Modal, Steps, Spin} from 'antd';
import {calculateParentId, getModelSteward, modelClone, modelPublish} from '../../../../../store/model';
import {useModelFileTreeLoader, useModelFileUploader} from '../../../../../store/modelFile';
import StepFilesCloneRedesigned from './StepFilesCloneRedesigned';
import StepFileTypes from './StepFileTypes';
import StepAccessRedesigned from './StepAccessRedesigned';
import StepMetadataCloneRedesigned from './StepMetadataCloneRedesigned';
import {PropertySelection} from './types';
import {useMounted} from '../../../../../hooks/useMounted';
import {messageAdd, MessageTypes} from '../../../../../store/message';
import {getUrlRoute, MRAppRoutes} from '../../../../router/routes';
import {useNavigate} from 'react-router-dom';
import {useNameCollisionCheckDialog} from '../ModelEdit/ModelEditSuffix/useNameCollisionCheckDialog';
import {ModelEditPermissionsFormData} from '../ModelEdit/ModelEditPermissons/ModelEditPermissionsForm';
import {modelLinksSetParent, useModelFlatHierarchy} from '../../../../../store/modelLinks';
import {userFindValueReturned} from '../../../../../store/user/utils';
import {ModelActionProps} from '../../../actionMenuTypes';
import {modelAccessUpdateList, useModelAccess} from '../../../../../store/modelAccess';
import {modelStewardSet} from '../../../../../store/modelSteward';
import NotificationConfirmationDialog from '../ModelEdit/ModelEditFiles/NotificationConfirmationDialog';

const STEP_LABELS = ['Metadata', 'Select Files', 'Add File Types', 'Access'];

const ModelCloneSideBarModalRedesigned: React.FC<ModelActionProps> = ({onCancel, onSuccess, model}) => {
  const dispatch = useDispatch();
  const mounted = useMounted();
  const navigate = useNavigate();
  const {checkFutureName, DialogComponent: NameCollisionDialogComponent} = useNameCollisionCheckDialog();
  const flatHierarchy = useModelFlatHierarchy(model.uuid);

  const [activeStep, setActiveStep] = useState(0);
  const [metadataValid, setMetadataValid] = useState(false);
  const [accessValid, setAccessValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isPublishedModel, setIsPublishedModel] = useState<boolean>(false);
  const [disableLoading, setDisableLoading] = useState(false);
  const [openNotifyDialog, setOpenNotifyDialog] = useState<boolean>(false);
  const [openWarningDialog, setOpenWarningDialog] = useState<boolean>(false);
  const [clonedModelUuid, setClonedModelUuid] = useState<string>();
  const [permissionsAutoPopulatedFromVvmc, setPermissionsAutoPopulatedFromVvmc] = useState(false);
  const [newFiles, setNewFiles] = useState<string[]>([]);

  const {data: fileTree, load: loadFileTree} = useModelFileTreeLoader(model.uuid, ['Parameter'], undefined);
  const modelAccess = useModelAccess(model.uuid);

  const [filesSelection, setFilesSelection] = useState<PropertySelection>({});
  const [newModel, setNewModel] = useState<ModelDTO>(model);
  const {start: saveFiles} = useModelFileUploader(newModel, fileTree ?? undefined);
  const [permissions, setPermissions] = useState<
    Pick<
      ModelEditPermissionsFormData,
      'owners' | 'viewers' | 'displayViewers' | 'calibrationModelers' | 'scenarioModelers' | 'vestLead'
    >
  >({
    owners: [],
    viewers: [],
    displayViewers: [],
    calibrationModelers: [],
    scenarioModelers: [],
    vestLead: undefined,
  });

  // Reset the autofill guard whenever the user picks a new VVMC document (owner hint changes)
  const prevOwnerHintRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const ownerHint = newModel.metadata?.owner?.trim();
    if (ownerHint !== prevOwnerHintRef.current) {
      prevOwnerHintRef.current = ownerHint;
      setPermissionsAutoPopulatedFromVvmc(false);
    }
  }, [newModel.metadata?.owner]);

  useEffect(() => {
    if (permissionsAutoPopulatedFromVvmc) {
      return;
    }

    const ownerHint = newModel.metadata?.owner?.trim();
    const vaiLeadHint = newModel.metadata?.vai_lead?.trim();
    const hedsLeadHint = newModel.metadata?.heds_lead?.trim();

    // Only autofill when the VVMC hints differ from the original cloned-from model
    const originalOwner = model.metadata?.owner?.trim();
    const originalVaiLead = model.metadata?.vai_lead?.trim();
    const originalHedsLead = model.metadata?.heds_lead?.trim();

    const hasNewHint =
      (ownerHint && ownerHint !== originalOwner) ||
      (vaiLeadHint && vaiLeadHint !== originalVaiLead) ||
      (hedsLeadHint && hedsLeadHint !== originalHedsLead);

    if (!hasNewHint) {
      return;
    }

    let cancelled = false;

    const resolveUser = async (hint?: string) => {
      if (!hint) {
        return null;
      }
      const users = await userFindValueReturned(dispatch, hint, {
        limit: 10,
        includeNpa: true,
        includeUnenrolled: true,
      });
      const normalized = hint.toLowerCase();
      const hintLocalPart = normalized.split('@')[0];
      const normalizePrincipal = (value?: string) => (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

      const exactMatch = users.find((u) => u.id?.toLowerCase() === normalized || u.email?.toLowerCase() === normalized);
      if (exactMatch) {
        return exactMatch;
      }

      const localPartMatch = users.find((u) => {
        const id = u.id?.toLowerCase() || '';
        const email = u.email?.toLowerCase() || '';
        const emailLocalPart = email.split('@')[0] || '';
        return (
          id === hintLocalPart ||
          emailLocalPart === hintLocalPart ||
          normalizePrincipal(id) === normalizePrincipal(hintLocalPart) ||
          normalizePrincipal(emailLocalPart) === normalizePrincipal(hintLocalPart)
        );
      });
      if (localPartMatch) {
        return localPartMatch;
      }

      const hintTokens = hintLocalPart.split(/[._-]+/).filter(Boolean);
      const tokenScoredCandidates = users
        .map((u) => {
          const id = (u.id || '').toLowerCase();
          const emailLocalPart = (u.email || '').toLowerCase().split('@')[0] || '';
          const haystack = `${id} ${emailLocalPart}`;
          const score = hintTokens.reduce((acc, token) => (haystack.includes(token) ? acc + 1 : acc), 0);
          return {u, score};
        })
        .filter((candidate) => candidate.score > 0)
        .sort((left, right) => right.score - left.score);

      if (tokenScoredCandidates.length === 1) {
        return tokenScoredCandidates[0].u;
      }
      if (tokenScoredCandidates.length > 1 && tokenScoredCandidates[0].score > tokenScoredCandidates[1].score) {
        return tokenScoredCandidates[0].u;
      }

      if (users.length === 1) {
        return users[0];
      }

      if (hint.includes('@')) {
        const localPart = hintLocalPart.trim();
        if (localPart) {
          return {
            id: localPart,
            email: hint,
          } as any;
        }
      }

      return null;
    };

    const autoPopulate = async () => {
      try {
        const [ownerUser, vestLeadUser] = await Promise.all([resolveUser(ownerHint), resolveUser(vaiLeadHint)]);

        if (cancelled) {
          return;
        }

        const resolvedOwner = ownerUser || vestLeadUser;
        const resolvedVestLead = vestLeadUser || ownerUser;

        if (resolvedOwner || resolvedVestLead) {
          setPermissions((prev) => ({
            ...prev,
            owners: resolvedOwner ? [resolvedOwner] : prev.owners,
            vestLead: resolvedVestLead || prev.vestLead,
          }));
        }
      } finally {
        if (!cancelled) {
          setPermissionsAutoPopulatedFromVvmc(true);
        }
      }
    };

    autoPopulate();

    return () => {
      cancelled = true;
    };
  }, [
    permissionsAutoPopulatedFromVvmc,
    newModel.metadata?.owner,
    newModel.metadata?.vai_lead,
    newModel.metadata?.heds_lead,
    model.metadata?.owner,
    model.metadata?.vai_lead,
    model.metadata?.heds_lead,
    dispatch,
  ]);

  const isExternalUserPresent = Object.values(permissions)
    .filter(Array.isArray)
    .some((role) => role.some((user: any) => user.userType === 'EXTERNAL_USER'));

  useEffect(() => {
    loadFileTree();
  }, [loadFileTree]);

  useEffect(() => {
    if (modelAccess.loaded) {
      setPermissions({
        vestLead: modelAccess.vestLead,
        owners: modelAccess.owners ?? [],
        calibrationModelers: modelAccess.calibrationModelers ?? [],
        scenarioModelers: modelAccess.scenarioModelers ?? [],
        viewers: (modelAccess.viewers as UserDTO[]) ?? [],
        displayViewers: (modelAccess.displayViewers as UserDTO[]) ?? [],
      });

      const steward = getModelSteward(model);
      const {vestLead} = modelAccess;
      setAccessValid(!!vestLead && !!steward);
    }
  }, [model, modelAccess]);

  useEffect(() => {
    if (fileTree) {
      setFilesSelection(
        fileTree.reduce((acc, val) => {
          if (!val.folder) {
            acc[val.uuid] = true;
          }
          return acc;
        }, {} as any as PropertySelection),
      );
    }
  }, [fileTree]);

  useEffect(() => {
    if (newFiles !== null) {
      newFiles.forEach((uuid) => {
        setFilesSelection((prev) => ({...prev, [uuid]: true}));
      });
    }
  }, [newFiles]);

  const handleNotifyCancel = useCallback(() => {
    setOpenNotifyDialog(false);
    onCancel?.();
    onSuccess?.();
    if (clonedModelUuid) {
      navigate(getUrlRoute(MRAppRoutes.MODEL_DETAIL, {uuid: clonedModelUuid}));
      window.location.reload();
    }
  }, [onSuccess, onCancel, clonedModelUuid, navigate]);

  const handleSubmit = useCallback(
    async (isPublished: boolean) => {
      setSubmitting(true);
      setDisableLoading(isPublished);
      // Only include UUIDs that exist in the original fileTree (already uploaded to backend).
      // Newly added files in Step 2 have local nanoid() UUIDs not known to the backend;
      // those are uploaded separately via saveFiles() after the clone.
      const fileTreeUuids = new Set((fileTree ?? []).map((f) => f.uuid));
      const params = {
        modelItemUuids: Object.keys(filesSelection).reduce((acc, uuid) => {
          if (filesSelection[uuid] && fileTreeUuids.has(uuid)) {
            acc.push(uuid);
          }
          return acc;
        }, [] as any as string[]),
        metadata: newModel.metadata,
        ownersIsids: permissions.owners.map((u) => u.id),
        viewersIsids: permissions.viewers.map((u) => u.id),
        displayViewersIsids: permissions.displayViewers.map((u) => u.id),
      };

      try {
        const newModelWithSuffix = await checkFutureName(newModel);
        const clonedModel = await modelClone(dispatch, model.uuid, {...params, metadata: newModelWithSuffix.metadata});
        setClonedModelUuid(clonedModel.uuid);

        // Run access, steward, and parent linking independently so a failure in one
        // does not block the others (parent linking was silently skipped when access failed).
        try {
          await modelAccessUpdateList(dispatch, clonedModel.uuid, {
            owners: permissions.owners,
            vestLead: permissions.vestLead,
            calibratorModeler: permissions.calibrationModelers,
            scenarioModeler: permissions.scenarioModelers,
            viewers: permissions.viewers,
            displayViewers: permissions.displayViewers,
          });
        } catch (e) {
          dispatch(messageAdd('Failed to update access permissions', MessageTypes.WARNING));
        }

        try {
          const modelSteward = getModelSteward(newModel);
          if (modelSteward) {
            await modelStewardSet(dispatch, clonedModel.uuid, modelSteward);
          }
        } catch (e) {
          dispatch(messageAdd('Failed to set model steward', MessageTypes.WARNING));
        }

        try {
          const parentId = calculateParentId(model, clonedModel, flatHierarchy);
          if (parentId) {
            await modelLinksSetParent(dispatch, clonedModel.uuid, parentId, true);
          }
        } catch (e) {
          dispatch(messageAdd('Failed to link parent model', MessageTypes.WARNING));
        }

        const {uuid} = clonedModel;
        try {
          await saveFiles({...newModel, uuid});
        } catch (e) {
          dispatch(messageAdd('Failed to save files', MessageTypes.WARNING));
        }

        if (isPublished) {
          await modelPublish(dispatch, clonedModel.uuid);
          dispatch(messageAdd('New Model Version created and Published', MessageTypes.SUCCESS));
        } else {
          dispatch(messageAdd('New Model Version created and saved as Draft', MessageTypes.SUCCESS));
        }

        setOpenNotifyDialog(true);
      } catch (e) {
        dispatch(messageAdd('Failed to create new model version', MessageTypes.ERROR));
      } finally {
        mounted.current && setSubmitting(false);
      }
    },
    [model, newModel, flatHierarchy, permissions, dispatch, filesSelection, mounted, checkFutureName, saveFiles],
  );

  const handleExternalUserAccess = () => {
    setOpenWarningDialog(false);
    handleSubmit(isPublishedModel);
  };

  const handleClick = (isPublished: boolean) => {
    setIsPublishedModel(isPublished);
    if (isExternalUserPresent) {
      setOpenWarningDialog(true);
    } else {
      handleSubmit(isPublished);
    }
  };

  const stepItems = STEP_LABELS.map((label, index) => {
    let status: 'finish' | 'error' | 'process' | 'wait' = 'wait';
    if (index === activeStep) {
      status = 'process';
    } else if (index === 0) {
      status = metadataValid ? 'finish' : 'error';
    } else if (index === 3) {
      status = accessValid ? 'finish' : 'error';
    } else if (index < activeStep) {
      status = 'finish';
    }
    return {title: label, status};
  });

  if (!modelAccess.loaded || !fileTree) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '48px'}}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      {NameCollisionDialogComponent}

      <div style={{padding: '24px 32px'}}>
        {/* Header */}
        <div style={{marginBottom: '24px'}}>
          <h2 style={{margin: 0, fontSize: '20px', fontWeight: 600}}>Add New Model Version</h2>
        </div>

        {/* Steps */}
        <Steps current={activeStep} items={stepItems} onChange={setActiveStep} style={{marginBottom: '32px'}} />

        {/* Step content */}
        <div style={{minHeight: '400px'}}>
          {activeStep === 0 && (
            <StepMetadataCloneRedesigned
              model={newModel}
              clonedFrom={model}
              files={fileTree}
              onChange={setNewModel}
              onStatusChange={setMetadataValid}
            />
          )}

          {activeStep === 1 && (
            <StepFilesCloneRedesigned
              files={fileTree}
              loadNewSelection={(modelItemUuids: string[]) => {
                setNewFiles(modelItemUuids);
              }}
              model={newModel}
              filesSelection={filesSelection}
              toggleFile={(uuid) => setFilesSelection({...filesSelection, [uuid]: !filesSelection[uuid]})}
            />
          )}

          {activeStep === 2 && (
            <StepFileTypes
              model={newModel}
              files={fileTree}
              filesSelection={filesSelection}
              toggleFile={(uuid) => setFilesSelection({...filesSelection, [uuid]: !filesSelection[uuid]})}
              onSubmit={(updatedModel, updatedFiles) => {
                setNewModel(updatedModel);
                setNewFiles(updatedFiles.map((f) => f.uuid));
              }}
            />
          )}

          {activeStep === 3 && (
            <StepAccessRedesigned
              permissions={{...permissions, model: newModel}}
              onChange={({model: m, ...newPermissions}) => {
                setPermissions(newPermissions);
                setNewModel(m);
                const steward = getModelSteward(m);
                const vestLead = newPermissions.vestLead;
                setAccessValid(!!vestLead && !!steward);
              }}
            />
          )}
        </div>

        {/* Footer buttons */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            marginTop: '24px',
            paddingTop: '24px',
            borderTop: '1px solid #f0f0f0',
          }}
        >
          <Button onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>

          {activeStep === 3 ? (
            <>
              <Button
                onClick={() => handleClick(false)}
                loading={submitting && !disableLoading}
                disabled={!metadataValid || !accessValid}
              >
                Save as Draft
              </Button>
              <Button
                type="primary"
                onClick={() => handleClick(true)}
                loading={submitting && disableLoading}
                disabled={!metadataValid || !accessValid}
                style={{background: '#00857c', borderColor: '#00857c'}}
              >
                Save and Publish
              </Button>
            </>
          ) : (
            <Button
              type="primary"
              onClick={() => setActiveStep(activeStep + 1)}
              disabled={activeStep === 0 ? !metadataValid : false}
              style={{background: '#00857c', borderColor: '#00857c'}}
            >
              Save & Proceed
            </Button>
          )}
        </div>
      </div>

      <NotificationConfirmationDialog
        open={openNotifyDialog}
        title="Notify Model Users ?"
        text="A new version of the model has been added. Do you want to notify users for this change?"
        description="An email notification will be sent to everyone who has permissions for this model entry."
        label="new model version"
        successMessage="Email notification of new model version sent to model users"
        uuid={clonedModelUuid}
        type="New Model Version"
        handleCancel={handleNotifyCancel}
      />

      <Modal
        open={openWarningDialog}
        title="External User Warning"
        onCancel={() => setOpenWarningDialog(false)}
        footer={[
          <Button key="back" onClick={() => setOpenWarningDialog(false)}>
            Go Back
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleExternalUserAccess}
            style={{background: '#00857c', borderColor: '#00857c'}}
          >
            {isPublishedModel ? 'Save and Publish' : 'Save as Draft'}
          </Button>,
        ]}
      >
        <p>External users will be granted access to the new model version. Do you wish to proceed?</p>
      </Modal>
    </>
  );
};

export default ModelCloneSideBarModalRedesigned;
